import { useState, useEffect } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { QUERY_NAME } from "@lhd/constants";

// Global axios loading state with URL tracking
let axiosLoadingCount = 0;
let axiosLoadingListeners: ((loading: boolean) => void)[] = [];
const activeAxiosUrls = new Set<string>();

// Filtered loading tracking by URL patterns
const filteredLoadingListeners = new Map<
  string,
  { patterns: string[]; listeners: ((loading: boolean) => void)[] }
>();
const filteredLoadingCounts = new Map<string, number>();

const checkUrlMatchesPatterns = (url: string, patterns: string[]): boolean => {
  return patterns.some((pattern) => url.includes(pattern));
};

const notifyFilteredListeners = (listenerId: string) => {
  const listenerData = filteredLoadingListeners.get(listenerId);
  if (!listenerData) return;

  const count = filteredLoadingCounts.get(listenerId) || 0;
  const isLoading = count > 0;
  listenerData.listeners.forEach((listener) => listener(isLoading));
};

export const setAxiosLoading = (loading: boolean, url?: string) => {
  if (loading && url) {
    activeAxiosUrls.add(url);
  } else if (!loading && url) {
    activeAxiosUrls.delete(url);
  }

  if (loading) {
    axiosLoadingCount++;
  } else {
    axiosLoadingCount = Math.max(0, axiosLoadingCount - 1);
  }

  const isLoading = axiosLoadingCount > 0;
  axiosLoadingListeners.forEach((listener) => listener(isLoading));

  // Check filtered listeners
  if (url) {
    filteredLoadingListeners.forEach((listenerData, listenerId) => {
      const matches = checkUrlMatchesPatterns(url, listenerData.patterns);
      if (matches) {
        const currentCount = filteredLoadingCounts.get(listenerId) || 0;
        const newCount = loading
          ? currentCount + 1
          : Math.max(0, currentCount - 1);
        filteredLoadingCounts.set(listenerId, newCount);
        notifyFilteredListeners(listenerId);
      }
    });
  }
};

export const useAxiosLoading = () => {
  const [isAxiosLoading, setIsAxiosLoading] = useState(axiosLoadingCount > 0);

  useEffect(() => {
    const listener = (loading: boolean) => {
      setIsAxiosLoading(loading);
    };

    axiosLoadingListeners.push(listener);

    return () => {
      axiosLoadingListeners = axiosLoadingListeners.filter(
        (l) => l !== listener,
      );
    };
  }, []);

  return isAxiosLoading;
};

const useFilteredAxiosLoading = (patterns: string[]) => {
  const [isLoading, setIsLoading] = useState(false);
  const [listenerId] = useState(
    () => `listener-${Date.now()}-${Math.random()}`,
  );

  useEffect(() => {
    const listener = (loading: boolean) => {
      setIsLoading(loading);
    };

    // Register listener with patterns
    if (!filteredLoadingListeners.has(listenerId)) {
      filteredLoadingListeners.set(listenerId, { patterns, listeners: [] });
      filteredLoadingCounts.set(listenerId, 0);
    }

    const listenerData = filteredLoadingListeners.get(listenerId);
    if (listenerData) {
      listenerData.patterns = patterns;
      listenerData.listeners.push(listener);
    }

    // Check initial state
    const initialCount = Array.from(activeAxiosUrls).filter((url) =>
      checkUrlMatchesPatterns(url, patterns),
    ).length;
    filteredLoadingCounts.set(listenerId, initialCount);
    setIsLoading(initialCount > 0);

    return () => {
      // Cleanup
      const listenerData = filteredLoadingListeners.get(listenerId);
      if (listenerData) {
        listenerData.listeners = listenerData.listeners.filter(
          (l) => l !== listener,
        );
        if (listenerData.listeners.length === 0) {
          filteredLoadingListeners.delete(listenerId);
          filteredLoadingCounts.delete(listenerId);
        }
      }
    };
  }, [listenerId, patterns]);

  return isLoading;
};

interface GlobalLoadingOptions {
  urlPatterns?: string[];
}

// Combined global loading hook with optional URL pattern filtering
export const useGlobalLoading = (options?: GlobalLoadingOptions) => {
  const isAxiosLoading = useAxiosLoading();
  const patterns = options?.urlPatterns || [];
  const isFilteredAxiosLoading = useFilteredAxiosLoading(patterns);

  // Filter React Query to only check for ME and queries containing "permission"
  const isReactQueryFetching =
    useIsFetching({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if (!queryKey || queryKey.length === 0) return false;

        const firstKey = String(queryKey[0]).toLowerCase();
        const allKeys = queryKey.map((k) => String(k).toLowerCase()).join(",");

        // Check if it's ME query or contains "permission"
        return (
          firstKey === QUERY_NAME.ME.toLowerCase() ||
          allKeys.includes("permission")
        );
      },
    }) > 0;

  const finalAxiosLoading =
    patterns.length > 0 ? isFilteredAxiosLoading : isAxiosLoading;

  return {
    isLoading: finalAxiosLoading || isReactQueryFetching,
    isAxiosLoading: finalAxiosLoading,
    isReactQueryFetching: isReactQueryFetching,
  };
};
